import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { UserPlus, Trash2, Eye, EyeOff, Edit, Upload, X } from 'lucide-react';
import DashboardLayout from '../DashboardLayout';
import axios from 'axios';

const ConfiguracionManagement = () => {
  const [config, setConfig] = useState({
    nombre_empresa: '',
    email_contacto: '',
    telefono: '',
    direccion: '',
    notificaciones_email: true,
    backup_automatico: true,
    modo_mantenimiento: false,
    version_sistema: 'v1.0.0',
    ultima_actualizacion: ''
  });
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Estados para logo y personalización
  const [logoConfig, setLogoConfig] = useState({
    nombre_organizacion: '',
    color_primario: '#3B82F6',
    color_secundario: '#1E40AF',
    mostrar_logo: false,
    logo_filename: null
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Estados para configuración de correo (SMTP / SendGrid)
  const [correoConfig, setCorreoConfig] = useState({
    proveedor: 'smtp',
    smtp_server: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_username: '',
    smtp_password_configurado: false,
    sendgrid_api_key_configurado: false,
    from_email: '',
    from_name: 'Sistema de Evaluación AOS'
  });
  const [smtpPasswordInput, setSmtpPasswordInput] = useState('');
  const [sendgridApiKeyInput, setSendgridApiKeyInput] = useState('');
  const [savingCorreo, setSavingCorreo] = useState(false);
  const [testEmailDestino, setTestEmailDestino] = useState('');
  const [testingCorreo, setTestingCorreo] = useState(false);

  // Estados para gestión de usuarios administradores
  const [usuarios, setUsuarios] = useState([]);
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    es_administrador: true,
    rol: 'admin'
  });
  const [editUser, setEditUser] = useState({
    username: '',
    email: '',
    password: '',
    es_administrador: true
  });

  // Cargar configuración al inicializar el componente
  useEffect(() => {
    loadConfig();
    loadUsuarios();
    loadLogoConfig();
    loadCorreoConfig();
  }, []);

  const loadLogoConfig = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';
      const response = await axios.get(`${API_URL}/configuracion/organizacion`);
      
      if (response.data.success) {
        setLogoConfig(response.data.data);
        
        // Cargar preview del logo si existe
        if (response.data.data.logo_filename && response.data.data.mostrar_logo) {
          setLogoPreview(`${API_URL}/configuracion/organizacion/logo?t=${Date.now()}`);
        }
      }
    } catch (error) {
      console.error('Error al cargar configuración de logo:', error);
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;

    // Validar tipo de archivo
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Use PNG, JPG, SVG o WebP');
      return;
    }

    // Validar tamaño (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo no debe exceder 5MB');
      return;
    }

    setLogoFile(file);

    // Crear preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadLogo = async () => {
    if (!logoFile) {
      toast.warning('Seleccione un archivo primero');
      return;
    }

    setUploadingLogo(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';
      const token = localStorage.getItem('dashboardToken');
      const formData = new FormData();
      formData.append('archivo', logoFile);

      const response = await axios.post(
        `${API_URL}/configuracion/organizacion/logo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast.success('Logo subido exitosamente');
        setLogoConfig(response.data.data);
        setLogoFile(null);
        loadLogoConfig();
      }
    } catch (error) {
      console.error('Error al subir logo:', error);
      toast.error(error.response?.data?.detail || 'Error al subir el logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('¿Está seguro de eliminar el logo?')) {
      return;
    }

    setUploadingLogo(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';
      const token = localStorage.getItem('dashboardToken');
      const response = await axios.delete(
        `${API_URL}/configuracion/organizacion/logo`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Logo eliminado exitosamente');
        setLogoPreview(null);
        setLogoFile(null);
        loadLogoConfig();
      }
    } catch (error) {
      console.error('Error al eliminar logo:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar el logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleUpdateLogoConfig = async () => {
    setUploadingLogo(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';
      const token = localStorage.getItem('dashboardToken');
      const formData = new FormData();
      formData.append('nombre_organizacion', logoConfig.nombre_organizacion || '');
      formData.append('color_primario', logoConfig.color_primario);
      formData.append('color_secundario', logoConfig.color_secundario);
      formData.append('mostrar_logo', logoConfig.mostrar_logo);

      const response = await axios.put(
        `${API_URL}/configuracion/organizacion`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast.success('Configuración actualizada exitosamente');
        setLogoConfig(response.data.data);
      }
    } catch (error) {
      console.error('Error al actualizar configuración:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar la configuración');
    } finally {
      setUploadingLogo(false);
    }
  };

  const loadCorreoConfig = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';
      const token = localStorage.getItem('dashboardToken');
      const response = await axios.get(`${API_URL}/configuracion/correo`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setCorreoConfig(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar configuración de correo:', error);
    }
  };

  const handleGuardarCorreo = async () => {
    setSavingCorreo(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';
      const token = localStorage.getItem('dashboardToken');

      const payload = {
        proveedor: correoConfig.proveedor,
        smtp_server: correoConfig.smtp_server,
        smtp_port: Number(correoConfig.smtp_port) || 587,
        smtp_username: correoConfig.smtp_username,
        from_email: correoConfig.from_email,
        from_name: correoConfig.from_name
      };

      // Los secretos solo se envían si el usuario escribió algo nuevo en
      // el campo -- dejarlo en blanco conserva el valor ya guardado.
      if (smtpPasswordInput) {
        payload.smtp_password = smtpPasswordInput;
      }
      if (sendgridApiKeyInput) {
        payload.sendgrid_api_key = sendgridApiKeyInput;
      }

      const response = await axios.put(`${API_URL}/configuracion/correo`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Configuración de correo guardada exitosamente');
        setCorreoConfig(response.data.data);
        setSmtpPasswordInput('');
        setSendgridApiKeyInput('');
      }
    } catch (error) {
      console.error('Error al guardar configuración de correo:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar la configuración de correo');
    } finally {
      setSavingCorreo(false);
    }
  };

  const handleEnviarPrueba = async () => {
    if (!testEmailDestino) {
      toast.error('Ingresa un correo de destino para la prueba');
      return;
    }

    setTestingCorreo(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';
      const token = localStorage.getItem('dashboardToken');

      const response = await axios.post(
        `${API_URL}/configuracion/correo/test`,
        { destinatario: testEmailDestino },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Correo de prueba enviado a ${testEmailDestino}`);
      }
    } catch (error) {
      console.error('Error al enviar correo de prueba:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar el correo de prueba');
    } finally {
      setTestingCorreo(false);
    }
  };


  const loadConfig = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001/api'}/config/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('dashboardToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      } else {
        toast.error('Error al cargar la configuración');
      }
    } catch (error) {
      toast.error('Error al conectar con el servidor');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001/api'}/config/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('dashboardToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('Configuración guardada exitosamente');
        // Recargar la configuración para mostrar los datos actualizados
        await loadConfig();
      } else {
        const errorData = await response.json();
        toast.error(`Error al guardar: ${errorData.detail || 'Error desconocido'}`);
      }
    } catch (error) {
      toast.error('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const loadUsuarios = async () => {
    try {
      const token = localStorage.getItem('dashboardToken');
      if (!token) {
        toast.error('No hay sesión activa. Por favor inicie sesión nuevamente.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001/api'}/admin/usuarios?page=1&per_page=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        toast.error('Sesión expirada. Redirigiendo al login...');
        localStorage.removeItem('dashboardToken');
        localStorage.removeItem('user');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setUsuarios(data.items || []);
      } else {
        const errorData = await response.json();
        toast.error(`Error al cargar usuarios: ${errorData.detail || 'Error desconocido'}`);
      }
    } catch (error) {
      // console.error('Error al cargar usuarios:', error);
      toast.error('Error al conectar con el servidor');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!newUser.username || !newUser.email || !newUser.password) {
      toast.error('Todos los campos son obligatorios');
      return;
    }

    if (newUser.password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('dashboardToken');
      if (!token) {
        toast.error('No hay sesión activa. Por favor inicie sesión nuevamente.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001/api'}/admin/usuarios`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          documento_identidad: newUser.username,
          email: newUser.email,
          password: newUser.password,
          es_administrador: true
        })
      });

      if (response.status === 401) {
        toast.error('Sesión expirada. Redirigiendo al login...');
        localStorage.removeItem('dashboardToken');
        localStorage.removeItem('user');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        setLoading(false);
        return;
      }

      if (response.ok) {
        toast.success('Usuario administrador creado exitosamente');
        setShowNewUserForm(false);
        setNewUser({
          username: '',
          email: '',
          password: '',
          es_administrador: true,
          rol: 'admin'
        });
        await loadUsuarios();
      } else {
        const errorData = await response.json();
        toast.error(`Error: ${errorData.detail || 'No se pudo crear el usuario'}`);
      }
    } catch (error) {
      // console.error('Error al crear usuario:', error);
      toast.error('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    
    if (!editingUser) return;

    // Validaciones
    if (!editUser.email) {
      toast.error('El email es obligatorio');
      return;
    }

    if (editUser.password && editUser.password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('dashboardToken');
      if (!token) {
        toast.error('No hay sesión activa. Por favor inicie sesión nuevamente.');
        setLoading(false);
        return;
      }

      const updateData = {
        email: editUser.email,
        es_administrador: editUser.es_administrador
      };

      // Solo incluir password si se proporcionó uno nuevo
      if (editUser.password) {
        updateData.password = editUser.password;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001/api'}/admin/usuarios/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.status === 401) {
        toast.error('Sesión expirada. Redirigiendo al login...');
        localStorage.removeItem('dashboardToken');
        localStorage.removeItem('user');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        setLoading(false);
        return;
      }

      if (response.ok) {
        toast.success('Usuario actualizado exitosamente');
        setEditingUser(null);
        setEditUser({
          username: '',
          email: '',
          password: '',
          es_administrador: true
        });
        await loadUsuarios();
      } else {
        const errorData = await response.json();
        toast.error(`Error: ${errorData.detail || 'No se pudo actualizar el usuario'}`);
      }
    } catch (error) {
      // console.error('Error al actualizar usuario:', error);
      toast.error('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const startEditUser = (usuario) => {
    setEditingUser(usuario);
    setEditUser({
      username: usuario.username,
      email: usuario.email,
      password: '',
      es_administrador: usuario.es_administrador
    });
    setShowEditPassword(false);
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setEditUser({
      username: '',
      email: '',
      password: '',
      es_administrador: true
    });
    setShowEditPassword(false);
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`¿Está seguro de eliminar al usuario "${username}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001/api'}/admin/usuarios/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('dashboardToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Usuario eliminado exitosamente');
        await loadUsuarios();
      } else {
        const errorData = await response.json();
        toast.error(`Error: ${errorData.detail || 'No se pudo eliminar el usuario'}`);
      }
    } catch (error) {
      toast.error('Error al conectar con el servidor');
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900">Configuración del Sistema</h1>
            <p className="mt-2 text-sm text-gray-700">
              Gestiona la configuración general del sistema
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-6">
          {/* Información de la empresa */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Información de la Empresa
              </h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nombre de la Empresa
                  </label>
                  <input
                    type="text"
                    value={config.nombre_empresa}
                    onChange={(e) => handleInputChange('nombre_empresa', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email de Contacto
                  </label>
                  <input
                    type="email"
                    value={config.email_contacto}
                    onChange={(e) => handleInputChange('email_contacto', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Teléfono
                  </label>
                  <input
                    type="text"
                    value={config.telefono}
                    onChange={(e) => handleInputChange('telefono', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Dirección
                  </label>
                  <input
                    type="text"
                    value={config.direccion}
                    onChange={(e) => handleInputChange('direccion', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Logo y Personalización de la Organización */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Logo y Personalización
              </h3>
              
              {/* Logo */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Logo de la Organización</h4>
                
                {logoPreview && (
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-32 h-32 border-2 border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 mb-2">
                        {logoConfig.logo_filename || 'Logo actual'}
                      </p>
                      <button
                        type="button"
                        onClick={handleDeleteLogo}
                        disabled={uploadingLogo}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Eliminar Logo
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-3">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                    onChange={handleLogoFileChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {logoFile && (
                    <button
                      type="button"
                      onClick={handleUploadLogo}
                      disabled={uploadingLogo}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploadingLogo ? 'Subiendo...' : 'Subir'}
                    </button>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Formatos: PNG, JPG, SVG, WebP. Tamaño máximo: 5MB
                </p>

                <div className="mt-3 flex items-center">
                  <input
                    type="checkbox"
                    id="mostrar_logo"
                    checked={logoConfig.mostrar_logo}
                    onChange={(e) => setLogoConfig({ ...logoConfig, mostrar_logo: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="mostrar_logo" className="ml-2 block text-sm text-gray-700">
                    Mostrar logo en el sistema
                  </label>
                </div>
              </div>

              {/* Nombre de la Organización */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de la Organización
                </label>
                <input
                  type="text"
                  value={logoConfig.nombre_organizacion || ''}
                  onChange={(e) => setLogoConfig({ ...logoConfig, nombre_organizacion: e.target.value })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                  placeholder="Ej: Mi Empresa S.A."
                />
              </div>

              {/* Colores */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Colores del Sistema</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Color Primario
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={logoConfig.color_primario}
                        onChange={(e) => setLogoConfig({ ...logoConfig, color_primario: e.target.value })}
                        className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={logoConfig.color_primario}
                        onChange={(e) => setLogoConfig({ ...logoConfig, color_primario: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="#3B82F6"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Color Secundario
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={logoConfig.color_secundario}
                        onChange={(e) => setLogoConfig({ ...logoConfig, color_secundario: e.target.value })}
                        className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={logoConfig.color_secundario}
                        onChange={(e) => setLogoConfig({ ...logoConfig, color_secundario: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="#1E40AF"
                      />
                    </div>
                  </div>
                </div>

                {/* Preview de colores */}
                <div className="mt-4 p-4 border border-gray-200 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">Vista previa:</p>
                  <div className="flex space-x-4">
                    <div
                      className="flex-1 h-16 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
                      style={{ backgroundColor: logoConfig.color_primario }}
                    >
                      Primario
                    </div>
                    <div
                      className="flex-1 h-16 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
                      style={{ backgroundColor: logoConfig.color_secundario }}
                    >
                      Secundario
                    </div>
                  </div>
                </div>
              </div>

              {/* Botón para guardar configuración de logo */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleUpdateLogoConfig}
                  disabled={uploadingLogo}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {uploadingLogo ? 'Guardando...' : 'Guardar Personalización'}
                </button>
              </div>
            </div>
          </div>

          {/* Configuración de Correo (SMTP / SendGrid) */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-1">
                Configuración de Correo
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Define cómo se envían los códigos de verificación (OTP) por correo. Cambiar esto aquí no requiere redeploy del servidor.
              </p>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Proveedor
                </label>
                <select
                  value={correoConfig.proveedor}
                  onChange={(e) => setCorreoConfig({ ...correoConfig, proveedor: e.target.value })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                >
                  <option value="smtp">SMTP (Gmail, Outlook, etc.)</option>
                  <option value="sendgrid">SendGrid</option>
                </select>
              </div>

              {correoConfig.proveedor === 'smtp' && (
                <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Servidor SMTP</label>
                    <input
                      type="text"
                      value={correoConfig.smtp_server || ''}
                      onChange={(e) => setCorreoConfig({ ...correoConfig, smtp_server: e.target.value })}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Puerto</label>
                    <input
                      type="number"
                      value={correoConfig.smtp_port || 587}
                      onChange={(e) => setCorreoConfig({ ...correoConfig, smtp_port: e.target.value })}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                      placeholder="587"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Usuario SMTP</label>
                    <input
                      type="text"
                      value={correoConfig.smtp_username || ''}
                      onChange={(e) => setCorreoConfig({ ...correoConfig, smtp_username: e.target.value })}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                      placeholder="tu-correo@aossas.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contraseña de aplicación
                      {correoConfig.smtp_password_configurado && (
                        <span className="ml-2 text-xs text-green-600">(ya configurada)</span>
                      )}
                    </label>
                    <input
                      type="password"
                      value={smtpPasswordInput}
                      onChange={(e) => setSmtpPasswordInput(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                      placeholder={correoConfig.smtp_password_configurado ? 'Déjalo en blanco para no cambiarla' : '••••••••••••••••'}
                    />
                  </div>
                </div>
              )}

              {correoConfig.proveedor === 'sendgrid' && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Key de SendGrid
                    {correoConfig.sendgrid_api_key_configurado && (
                      <span className="ml-2 text-xs text-green-600">(ya configurada)</span>
                    )}
                  </label>
                  <input
                    type="password"
                    value={sendgridApiKeyInput}
                    onChange={(e) => setSendgridApiKeyInput(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                    placeholder={correoConfig.sendgrid_api_key_configurado ? 'Déjalo en blanco para no cambiarla' : 'SG.xxxxxxxxxxxxxxxx'}
                  />
                </div>
              )}

              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Correo remitente</label>
                  <input
                    type="email"
                    value={correoConfig.from_email || ''}
                    onChange={(e) => setCorreoConfig({ ...correoConfig, from_email: e.target.value })}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                    placeholder="notificaciones@aossas.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del remitente</label>
                  <input
                    type="text"
                    value={correoConfig.from_name || ''}
                    onChange={(e) => setCorreoConfig({ ...correoConfig, from_name: e.target.value })}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                    placeholder="Sistema de Evaluación AOS"
                  />
                </div>
              </div>

              <div className="flex justify-end mb-6">
                <button
                  type="button"
                  onClick={handleGuardarCorreo}
                  disabled={savingCorreo}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {savingCorreo ? 'Guardando...' : 'Guardar Configuración de Correo'}
                </button>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Enviar correo de prueba</h4>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    value={testEmailDestino}
                    onChange={(e) => setTestEmailDestino(e.target.value)}
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                    placeholder="destino@correo.com"
                  />
                  <button
                    type="button"
                    onClick={handleEnviarPrueba}
                    disabled={testingCorreo}
                    className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
                  >
                    {testingCorreo ? 'Enviando...' : 'Enviar Prueba'}
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Usa la configuración ya guardada arriba -- guarda primero los cambios si acabas de editarlos.
                </p>
              </div>
            </div>
          </div>

          {/* Configuración del sistema */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Configuración del Sistema
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Notificaciones por Email</h4>
                    <p className="text-sm text-gray-500">Enviar notificaciones automáticas por correo electrónico</p>
                  </div>
                  <button
                    type="button"
                    className={`${
                      config.notificaciones_email ? 'bg-indigo-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                    onClick={() => handleInputChange('notificaciones_email', !config.notificaciones_email)}
                  >
                    <span className="sr-only">Usar notificaciones</span>
                    <span
                      className={`${
                        config.notificaciones_email ? 'translate-x-5' : 'translate-x-0'
                      } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Backup Automático</h4>
                    <p className="text-sm text-gray-500">Realizar copias de seguridad automáticas diariamente</p>
                  </div>
                  <button
                    type="button"
                    className={`${
                      config.backup_automatico ? 'bg-indigo-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                    onClick={() => handleInputChange('backup_automatico', !config.backup_automatico)}
                  >
                    <span className="sr-only">Backup automático</span>
                    <span
                      className={`${
                        config.backup_automatico ? 'translate-x-5' : 'translate-x-0'
                      } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Modo Mantenimiento</h4>
                    <p className="text-sm text-gray-500">Activar modo mantenimiento para el sistema</p>
                  </div>
                  <button
                    type="button"
                    className={`${
                      config.modo_mantenimiento ? 'bg-red-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2`}
                    onClick={() => handleInputChange('modo_mantenimiento', !config.modo_mantenimiento)}
                  >
                    <span className="sr-only">Modo mantenimiento</span>
                    <span
                      className={`${
                        config.modo_mantenimiento ? 'translate-x-5' : 'translate-x-0'
                      } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Información del sistema */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Información del Sistema
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Versión</dt>
                  <dd className="mt-1 text-sm text-gray-900">{config.version_sistema || 'v1.0.0'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Última actualización</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {config.ultima_actualizacion 
                      ? new Date(config.ultima_actualizacion).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : '18 de Noviembre, 2024'
                    }
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Estado del sistema</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      config.modo_mantenimiento 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {config.modo_mantenimiento ? 'En mantenimiento' : 'Operativo'}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Backup automático</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      config.backup_automatico 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {config.backup_automatico ? 'Activo' : 'Inactivo'}
                    </span>
                  </dd>
                </div>
              </div>
            </div>
          </div>

          {/* Gestión de Usuarios Administradores */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Usuarios Administradores
                </h3>
                <button
                  onClick={() => setShowNewUserForm(!showNewUserForm)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Agregar Usuario
                </button>
              </div>

              {/* Formulario de nuevo usuario */}
              {showNewUserForm && (
                <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <h4 className="text-sm font-medium text-gray-900 mb-4">Nuevo Usuario Administrador</h4>
                  <form onSubmit={handleCreateUser}>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Nombre de Usuario *
                        </label>
                        <input
                          type="text"
                          value={newUser.username}
                          onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Email *
                        </label>
                        <input
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                          required
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Contraseña *
                        </label>
                        <div className="mt-1 relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={newUser.password}
                            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm pr-10 px-3 py-2 border"
                            required
                            minLength={8}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">Mínimo 8 caracteres</p>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewUserForm(false);
                          setNewUser({
                            username: '',
                            email: '',
                            password: '',
                            es_administrador: true,
                            rol: 'admin'
                          });
                        }}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {loading ? 'Creando...' : 'Crear Usuario'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Formulario de edición de usuario */}
              {editingUser && (
                <div className="mb-6 p-4 border border-blue-200 rounded-lg bg-blue-50">
                  <h4 className="text-sm font-medium text-gray-900 mb-4">Editar Usuario: {editingUser.username}</h4>
                  <form onSubmit={handleEditUser}>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Nombre de Usuario
                        </label>
                        <input
                          type="text"
                          value={editUser.username}
                          disabled
                          className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm sm:text-sm px-3 py-2 border"
                        />
                        <p className="mt-1 text-xs text-gray-500">El nombre de usuario no se puede cambiar</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Email *
                        </label>
                        <input
                          type="email"
                          value={editUser.email}
                          onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                          required
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Nueva Contraseña (dejar en blanco para no cambiar)
                        </label>
                        <div className="mt-1 relative">
                          <input
                            type={showEditPassword ? 'text' : 'password'}
                            value={editUser.password}
                            onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm pr-10 px-3 py-2 border"
                            minLength={8}
                            placeholder="Dejar en blanco para mantener la actual"
                          />
                          <button
                            type="button"
                            onClick={() => setShowEditPassword(!showEditPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          >
                            {showEditPassword ? (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">Mínimo 8 caracteres si desea cambiarla</p>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        {loading ? 'Guardando...' : 'Guardar Cambios'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Lista de usuarios */}
              <div className="mt-4">
                {usuarios.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No hay usuarios administradores</p>
                ) : (
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Usuario</th>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Email</th>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Rol</th>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Estado</th>
                          <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                            <span className="sr-only">Acciones</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {usuarios.map((usuario) => (
                          <tr key={usuario.id}>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                              {usuario.username}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {usuario.email}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                {usuario.rol || 'admin'}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                usuario.is_active 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {usuario.is_active ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => startEditUser(usuario)}
                                  className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                                >
                                  <Edit className="h-4 w-4 mr-1" />
                                  Editar
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(usuario.id, usuario.username)}
                                  className="text-red-600 hover:text-red-900 inline-flex items-center"
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Eliminar
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => loadConfig()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Recargar
            </button>
            <button
              type="button"
              onClick={handleSaveConfig}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Guardando...' : 'Guardar Configuración'}
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ConfiguracionManagement;
